import { closeDrawer, isOpenInfoModal, isReportDrawerOpen, reportFormDataInnit, reportListData, selectedInfoModalTab } from 'Pages/ReportsAlerts/Reports/@signals'
import { INFO_MODAL_TABS } from 'Pages/ReportsAlerts/Reports/@constants'

// ─── Modal ranges ─────────────────────────────────────────────────────────────
// Steps 3-19 require the report creation drawer to be open.
export const MODAL_RANGES = [
	{
		from: 3, to: 19,
		open: () => reportFormDataInnit(),
		close: () => closeDrawer(),
		isOpen: () => isReportDrawerOpen.value === true,
	},
	// Steps 33-35 require the Info Report modal to be open.
	{
		from: 33, to: 35,
		open: () => {
			const firstReport = reportListData.value[0]
			if (firstReport) {
				selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS
				isOpenInfoModal.value = firstReport.idReport
			}
		},
		close: () => { isOpenInfoModal.value = null },
		isOpen: () => !!isOpenInfoModal.value && !!document.querySelector('.MuiDialog-root'),
	},
]

// ─── DOM helpers ────────────────────────────────────────────────────────────

function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findTableColumn(headerText) {
	const headers = document.querySelectorAll('th, [class*="MUIDataTableHeadCell"]')
	for (const h of headers) {
		if (h.textContent.trim().toLowerCase().includes(headerText.toLowerCase())) {
			return h
		}
	}
	return null
}

// ─── Language detection ──────────────────────────────────────────────────────

const ES = navigator.language?.startsWith('es')

// Close drawer on tour skip/close during drawer section
const drawerCleanup = () => closeDrawer()

// ─── Steps ───────────────────────────────────────────────────────────────────

export const TOUR_STEPS = [
	// ─── 0: Intro ───────────────────────────────────────────────────────────────
	{
		type: 'intro',
		icon: 'route',
		title: ES ? 'Bienvenido a Informes Extra' : 'Welcome to Extra Reports',
		description: ES
			? 'Los Informes Extra gestionan busquedas de rutas de solo ida — donde las ubicaciones de recogida y devolucion son diferentes. Monitoriza precios de rutas entre ciudades y entre paises en multiples sitios.'
			: 'Extra Reports manage one-way route searches — where the pickup and dropoff locations are different. Monitor pricing for cross-city and cross-country rental routes across multiple sites.',
	},
	// ─── 1: Icons Legend ────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Download Report')
			if (!el) return null
			let parent = el.parentElement
			for (let i = 0; i < 6; i++) {
				if (!parent) break
				const text = parent.textContent.toLowerCase()
				if (text.includes('download report') && text.includes('download export')) {
					if (parent.children.length >= 3) return parent
				}
				parent = parent.parentElement
			}
			return el.parentElement
		},
		icon: 'icons',
		title: ES ? 'Leyenda de Iconos' : 'Icons Legend',
		description: ES
			? 'La leyenda muestra los iconos de descarga disponibles en la columna Fecha. Los informes extra se centran en la recopilacion de datos — descarga el informe completo o la exportacion filtrada una vez finalizada la ejecucion.'
			: 'The legend shows download icons available in the Date column. Extra reports focus on data collection — download the full report or the filtered export after execution completes.',
		example: ES
			? 'Descargar Informe | Descargar Exportacion | Multi-informe | Multi-exportacion'
			: 'Download Report | Download Export | Multi-report | Multi-export',
	},
	// ─── 2: Create Extra Report — opens the drawer ──────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const btn = findByText('Create Report')
			return btn?.closest('button') || btn?.parentElement
		},
		icon: 'file-plus',
		title: ES ? 'Crear Informe Extra' : 'Create Extra Report',
		description: ES
			? 'Este boton abre el formulario de creacion configurado para rutas de solo ida. Haz clic en "Abrir Formulario" y recorreremos cada campo juntos: nombre, sitios, oficinas, rutas de solo ida, fechas, filtros y frecuencia.'
			: 'This button opens the creation form configured for one-way routes. Click "Open Form" and we will walk through each field together: name, sites, offices, one-way routes, dates, filters, and frequency.',
		action: () => reportFormDataInnit(),
		waitFor: () => isReportDrawerOpen.value === true,
	},
	// ─── 3: Drawer overview ─────────────────────────────────────────────────────
	{
		type: 'intro',
		icon: 'rectangle-list',
		title: ES ? 'Formulario de Creacion' : 'Creation Form',
		description: ES
			? 'El formulario tiene cinco secciones: Nombre, Sitios y Oficinas, Configuracion de Busqueda, Filtros de Exportacion y Frecuencia. Vamos campo por campo.'
			: 'The form has five sections: Name, Sites & Offices, Search Configuration, Export Filters, and Frequency. Let\'s go field by field.',
		reverseAction: () => closeDrawer(),
		cleanup: drawerCleanup,
	},
	// ─── 4: Num. Searches info button ────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-num-searches-info"]'),
		icon: 'circle-info',
		title: ES ? 'Informacion de Busquedas' : 'Search Info',
		description: ES
			? 'El indicador "Num. Searches" muestra el total de busquedas que ejecutara este informe. Pulsa el icono para ver el desglose detallado: numero de oficinas, busquedas por oficina, fechas de recogida primera y ultima, y el listado completo de fechas de pickup.'
			: 'The "Num. Searches" indicator shows the total searches this report will execute. Click the info icon to see the detailed breakdown: number of offices, searches per office, first and last pickup dates, and the full pickup date list.',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 1 — Report Name
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 5: Report Name field ───────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-report-name"]'),
		icon: 'pen-line',
		title: ES ? 'Nombre del Informe' : 'Report Name',
		description: ES
			? 'El nombre que identifica este informe en la lista. Usa nombres descriptivos que incluyan la ruta, por ejemplo "Madrid → Barcelona Semanal". Minimo 5 caracteres, maximo 100.'
			: 'The name that identifies this report in the list. Use descriptive names that include the route, e.g. "Madrid → Barcelona Weekly". Minimum 5 characters, maximum 100.',
		example: ES ? 'Ej: Madrid → Barcelona Airport Weekly' : 'E.g.: Madrid → Barcelona Airport Weekly',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 2 — Sites & Offices
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 6: Use Office Group switch ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-office-switch"]'),
		icon: 'toggle-off',
		title: ES ? 'Modo de Oficina' : 'Office Mode',
		description: ES
			? 'Elige como seleccionar las oficinas de recogida. Desactivado: seleccionas oficinas individuales. Activado: usas un grupo de oficinas predefinido que cubre varias ubicaciones. Los grupos son mas practicos para monitorizar mercados completos.'
			: 'Choose how to select pickup offices. Off: pick individual offices. On: use a predefined office group covering multiple locations. Groups are more practical for monitoring entire markets.',
		example: ES ? 'Activado = Grupo | Desactivado = Oficinas individuales' : 'On = Group | Off = Individual offices',
		cleanup: drawerCleanup,
	},
	// ─── 7: Pickup Office / Group Office ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-office-field"]'),
		icon: 'building',
		title: ES ? 'Oficinas de Recogida' : 'Pickup Offices',
		description: ES
			? 'Las oficinas (o el grupo) de recogida que quieres monitorizar. Para rutas de solo ida, esta es la ubicacion de inicio. El numero de oficinas multiplicado por sitios y fechas determina el total de busquedas.'
			: 'The pickup offices (or group) you want to monitor. For one-way routes, this is the starting location. The number of offices multiplied by sites and dates determines the total searches.',
		cleanup: drawerCleanup,
	},
	// ─── 8: Sites ───────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-site"]'),
		icon: 'globe',
		title: ES ? 'Sitios Web' : 'Sites',
		description: ES
			? 'Las webs de alquiler de coches que quieres monitorizar para rutas de solo ida. No todos los sitios admiten busquedas de solo ida — los disponibles aqui estan filtrados a los que gestionan recogida y devolucion en ubicaciones diferentes.'
			: 'The car rental websites to monitor for one-way routes. Not all sites support one-way searches — the available ones here are filtered to those that handle different pickup and dropoff locations.',
		cleanup: drawerCleanup,
	},
	// ─── 9: Currency ────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-currency"]'),
		icon: 'euro-sign',
		title: ES ? 'Moneda' : 'Currency',
		description: ES
			? 'La moneda en la que se mostraran los precios en los resultados del informe. Selecciona la moneda que corresponda al mercado de la ruta que estas monitorizando.'
			: 'The currency in which prices will be shown in the report results. Select the currency that matches the market of the route you are monitoring.',
		cleanup: drawerCleanup,
	},
	// ─── 10: One-Way Routes field ─────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-oneway"]'),
		icon: 'right-long',
		title: ES ? 'Rutas de Solo Ida' : 'One-Way Routes',
		description: ES
			? 'Define las rutas de solo ida: combina cada oficina de recogida con una oficina de devolucion diferente. Aqui configuras el par recogida → devolucion que define cada ruta a monitorizar. Es el campo clave que diferencia los informes extra de los estandar.'
			: 'Define the one-way routes: pair each pickup office with a different dropoff office. Here you configure the pickup → dropoff pair that defines each route to monitor. This is the key field that differentiates extra reports from standard ones.',
		example: ES ? 'MAD (Madrid) → BCN (Barcelona)' : 'MAD (Madrid) → BCN (Barcelona)',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 3 — Search Configuration
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 11: Use CSV switch ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-csv-switch"]'),
		icon: 'file-csv',
		title: ES ? 'Modo de Busqueda: Fecha o CSV' : 'Search Mode: Date or CSV',
		description: ES
			? 'Por defecto el informe genera busquedas a partir de la configuracion de fechas. Si activas "Use CSV", puedes subir un fichero con busquedas completamente personalizadas — util para rutas especificas que no caben en la configuracion estandar.'
			: 'By default the report generates searches from the date configuration. If you enable "Use CSV", you can upload a file with fully custom searches — useful for specific routes that do not fit the standard configuration.',
		cleanup: drawerCleanup,
	},
	// ─── 12: Pickup Dates mode selector ─────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-pickup-dates"]'),
		icon: 'calendar-range',
		title: ES ? 'Modo de Fechas de Recogida' : 'Pickup Dates Mode',
		description: ES
			? 'Define como se seleccionan las fechas de recogida. Cuatro opciones: dias especificos, lead times (x dias desde hoy), dia y semana, o proximo mes completo. Cada modo genera un subformulario diferente adaptado al tipo de ruta.'
			: 'Defines how pickup dates are selected. Four options: specific days, lead times (x days from today), day and week, or next full month. Each mode shows a different sub-form adapted to the route type.',
		example: ES
			? 'Lead times: 1,3,7,14,30 dias | Especificos: 15/06, 22/06...'
			: 'Lead times: 1,3,7,14,30 days | Specific: 15/06, 22/06...',
		cleanup: drawerCleanup,
	},
	// ─── 13: Rental length ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-rental-length"]'),
		icon: 'moon',
		title: ES ? 'Duracion del Alquiler' : 'Rental Length',
		description: ES
			? 'El numero de noches del alquiler que se buscaran. Puedes seleccionar varias duraciones a la vez. Para rutas de solo ida las duraciones mas habituales son cortas (1-3 noches). Cada duracion combinada con cada fecha genera una busqueda independiente.'
			: 'The number of rental nights to search. You can select multiple durations at once. For one-way routes the most common durations are short (1-3 nights). Each duration combined with each date generates an independent search.',
		example: ES ? 'Ej: 1, 3, 7 noches → 3x busquedas por fecha' : 'E.g.: 1, 3, 7 nights → 3x searches per date',
		cleanup: drawerCleanup,
	},
	// ─── 14: Days to monitor ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-days-monitor"]'),
		icon: 'calendar-days',
		title: ES ? 'Dias a Monitorizar' : 'Days to Monitor',
		description: ES
			? 'Cuantos dias consecutivos a partir de hoy incluira el informe. Por ejemplo, 30 dias significa que se buscaran precios para los proximos 30 dias de recogida. Maximo 365 dias.'
			: 'How many consecutive days from today the report will include. For example, 30 days means prices will be searched for the next 30 pickup days. Maximum 365 days.',
		cleanup: drawerCleanup,
	},
	// ─── 15: Pickup Time / Dropoff Time ─────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-pickup-time"]'),
		icon: 'clock',
		title: ES ? 'Hora de Recogida y Devolucion' : 'Pickup & Dropoff Time',
		description: ES
			? 'La hora de recogida y la hora de devolucion que se usaran en las busquedas. En rutas de solo ida la hora de devolucion puede no ser relevante para el precio, pero algunos sitios la requieren. Configuralas para obtener precios comparables.'
			: 'The pickup time and dropoff time used in searches. For one-way routes the dropoff time may not affect pricing, but some sites require it. Configure them to get comparable prices.',
		example: ES ? 'Recogida: 10:00 | Devolucion: 10:00' : 'Pickup: 10:00 | Dropoff: 10:00',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 4 — Export Filters (optional)
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 16: Export Filters section ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-export-filters"]'),
		icon: 'sliders',
		title: ES ? 'Filtros de Exportacion (Opcional)' : 'Export Filters (Optional)',
		description: ES
			? 'Seccion opcional para filtrar los resultados del Excel. Permite elegir entre filtrado por categoria de vehiculo o por codigo ACRISS. Tambien puedes establecer un limite de horas y activar la generacion automatica del informe tras cada ejecucion.'
			: 'Optional section to filter Excel results. Allows choosing between filtering by vehicle category or ACRISS code. You can also set an hours limit and enable automatic report generation after each execution.',
		example: ES ? 'Categorias: Economy, Compact | ACRISS: ECMR, CCMR' : 'Categories: Economy, Compact | ACRISS: ECMR, CCMR',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 5 — Frequency & Notification
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 17: Email ──────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-email"]'),
		icon: 'envelope',
		title: ES ? 'Email de Notificacion' : 'Notification Email',
		description: ES
			? 'Los correos electronicos a los que se enviaran los resultados del informe cuando se complete la ejecucion. Puedes anadir varios emails — pulsa Enter o escribe una coma entre cada uno. Por defecto se rellena con tu email de usuario.'
			: 'The email addresses that will receive report results when execution completes. You can add multiple emails — press Enter or type a comma between each. Defaults to your user email.',
		cleanup: drawerCleanup,
	},
	// ─── 18: Execution frequency (hours) ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-exec-frequency"]'),
		icon: 'clock-rotate-left',
		title: ES ? 'Horas de Ejecucion' : 'Execution Hours',
		description: ES
			? 'Las horas del dia en las que se ejecutara automaticamente el informe. Las rutas de solo ida suelen tener menos volatilidad de precios, por lo que una o dos comprobaciones diarias suelen ser suficientes.'
			: 'The times of day when the report will run automatically. One-way routes typically have less price volatility, so one or two daily checks are usually sufficient.',
		example: ES ? 'Ej: 08:00, 18:00 → dos capturas al dia' : 'E.g.: 08:00, 18:00 → two daily snapshots',
		cleanup: drawerCleanup,
	},
	// ─── 19: Frequencies (days) + close drawer ───────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-frequencies"]'),
		icon: 'calendar-week',
		title: ES ? 'Dias de Ejecucion' : 'Execution Days',
		description: ES
			? 'Los dias de la semana en los que se ejecutara el informe. Combinado con las horas de ejecucion define el calendario completo. Cuando lo tengas todo configurado, guarda con el boton "Save" del pie del formulario.'
			: 'The days of the week when the report will run. Combined with execution hours this defines the complete schedule. When everything is configured, save using the "Save" button at the bottom of the form.',
		example: ES ? 'Lunes, Miercoles, Viernes' : 'Monday, Wednesday, Friday',
		action: () => closeDrawer(),
		waitFor: () => !isReportDrawerOpen.value,
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// TABLE SECTION
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 20: Extra Reports Table ────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Reports Extra List') || findByText('Reports extra List')
			if (el) {
				const card = el.closest('.MuiPaper-root') || el.closest('.MuiCard-root')
				if (card) return card
			}
			return document.querySelector('.MuiTable-root')?.closest('.MuiPaper-root')
		},
		icon: 'table-cells',
		title: ES ? 'Tabla de Informes Extra' : 'Extra Reports Table',
		description: ES
			? 'Todos tus informes de rutas de solo ida listados aqui. Cada fila muestra la configuracion del informe incluyendo la ruta recogida-devolucion, los sitios monitorizados, la programacion y el estado de ejecucion actual.'
			: 'All your one-way route reports listed here. Each row shows the report configuration including the pickup-dropoff route, monitored sites, schedule, and current execution status.',
	},
	// ─── 21: Report Name column ─────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Report name'),
		icon: 'tag',
		title: ES ? 'Nombre del Informe' : 'Report Name',
		description: ES
			? 'El nombre de cada informe extra. Usa nombres claros que incluyan la ruta (p. ej., "Madrid → Barcelona Semanal") para identificar rapidamente los informes de solo ida en la lista.'
			: 'The name of each extra report. Use clear naming that includes the route (e.g., "Madrid → Barcelona Weekly") to quickly identify one-way reports in the list.',
	},
	// ─── 22: Search numbers column ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Search numbers') || findTableColumn('Searches'),
		icon: 'hashtag',
		title: ES ? 'Numero de Busquedas' : 'Search Numbers',
		description: ES
			? 'Total de busquedas por ejecucion. Las rutas de solo ida suelen generar mas busquedas ya que cada combinacion recogida-devolucion es una busqueda independiente. Los informes con mas de 1.000 muestran un icono de fuego como advertencia.'
			: 'Total searches per execution. One-way routes often generate more searches since each pickup-dropoff combination is a separate search. Reports with 1,000+ show a fire icon warning.',
		example: ES
			? 'Menos de 1K = Normal | 1K+ = Advertencia de fuego'
			: 'Under 1K = Normal | 1K+ = Fire warning',
	},
	// ─── 23: Sites column ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Sites'),
		icon: 'globe',
		title: ES ? 'Sitios' : 'Sites',
		description: ES
			? 'Los sitios web monitorizados para precios de rutas de solo ida. No todos los sitios admiten busquedas de solo ida — los sitios disponibles aqui estan filtrados a los que gestionan ubicaciones de recogida y devolucion diferentes.'
			: 'The websites being monitored for one-way route pricing. Not all sites support one-way searches — the available sites here are filtered to those that handle different pickup and dropoff locations.',
	},
	// ─── 24: Frequency Hours column ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Frequency Hours') || findTableColumn('Hours'),
		icon: 'clock',
		title: ES ? 'Horas de Frecuencia' : 'Frequency Hours',
		description: ES
			? 'Horas de ejecucion programadas. Las rutas de solo ida suelen tener menos volatilidad de precios que las rutas estandar, por lo que pueden ser suficientes menos comprobaciones diarias.'
			: 'Scheduled execution times. One-way routes typically have less price volatility than standard routes, so fewer daily checks may be sufficient.',
	},
	// ─── 25: Frequency Days column ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Frequency'),
		icon: 'calendar-days',
		title: ES ? 'Dias de Frecuencia' : 'Frequency Days',
		description: ES
			? 'Dias de la semana en los que se ejecuta el informe. Combinado con las horas, esto define el calendario de monitorizacion completo para cada ruta de solo ida.'
			: 'Days of the week when the report executes. Combined with the hours, this defines the complete monitoring schedule for each one-way route.',
	},
	// ─── 26: Pick up & Drop off route column ────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Pick up') || findTableColumn('Airports'),
		icon: 'location-dot',
		title: ES ? 'Ruta de Recogida y Devolucion' : 'Pick up & Drop off Route',
		description: ES
			? 'La columna clave de los informes extra — muestra tanto la ubicacion de recogida como la de devolucion. A diferencia de los informes estandar, estos siempre tienen oficinas de recogida y devolucion diferentes que definen la ruta de solo ida.'
			: 'The key column for extra reports — shows both the pickup and dropoff locations. Unlike standard reports, these always have different pickup and dropoff offices defining the one-way route.',
		example: ES
			? 'Aeropuerto de Madrid (MAD) → Aeropuerto de Barcelona (BCN)'
			: 'Madrid Airport (MAD) → Barcelona Airport (BCN)',
	},
	// ─── 27: Date & Execution Status column ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Date'),
		icon: 'calendar-check',
		title: ES ? 'Fecha y Estado de Ejecucion' : 'Date & Execution Status',
		description: ES
			? 'Ultima fecha de ejecucion y estado: Listo (completado, descarga disponible), En ejecucion (barra de progreso con contador de tareas) o Detenido. Haz clic en la barra de progreso durante la ejecucion para ver los detalles de las busquedas.'
			: 'Last execution date and status: Ready (completed, download available), Running (progress bar with task count), or Stopped. Click the progress bar during execution to view search details.',
		example: ES
			? 'Listo = Descargas | En ejecucion = Progreso | Detenido = Parado'
			: 'Ready = Downloads | Running = Progress | Stopped = Halted',
	},
	// ─── 28: Download Report button ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-report"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-report"]'),
		icon: 'cloud-arrow-down',
		title: ES ? 'Descargar Informe' : 'Download Report',
		description: ES
			? 'El icono verde descarga el informe completo en Excel con todos los datos de la ejecucion. Disponible solo cuando el estado es "Listo". El fichero incluye todos los precios encontrados ordenados por fecha, sitio y tipo de vehiculo.'
			: 'The green icon downloads the full report in Excel with all execution data. Available only when the status is "Ready". The file includes all prices found sorted by date, site, and vehicle type.',
	},
	// ─── 29: Download Export button ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-export"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-export"]'),
		icon: 'file-arrow-down',
		title: ES ? 'Descargar Exportacion' : 'Download Export',
		description: ES
			? 'El icono azul descarga la exportacion filtrada segun los filtros de exportacion configurados (categorias o ACRISS). Util cuando solo necesitas un subconjunto de los datos de la ejecucion, ya filtrado y listo para analizar.'
			: 'The blue icon downloads the filtered export according to the export filters configured (categories or ACRISS). Useful when you only need a subset of the execution data, already filtered and ready to analyze.',
	},
	// ─── 30: Enable / Disable toggle ────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const sw = document.querySelector('.MuiSwitch-root')
			if (sw) return sw.closest('td') || sw
			return findTableColumn('Off/On') || findTableColumn('On')
		},
		icon: 'toggle-on',
		title: ES ? 'Activar / Desactivar' : 'Enable / Disable',
		description: ES
			? 'Activa o desactiva un informe. Los informes desactivados conservan su configuracion pero no se ejecutaran segun la programacion. Util para rutas estacionales que solo necesitas en periodos de alta demanda.'
			: 'Toggle a report on or off. Disabled reports keep their configuration but won\'t execute on schedule. Useful for seasonal routes you only need during peak periods.',
	},
	// ─── 31: Row Actions ────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Actions'),
		icon: 'gears',
		title: ES ? 'Acciones de Fila' : 'Row Actions',
		description: ES
			? 'Acciones por informe: Ejecutar (ejecutar ahora), Editar (cambiar configuracion), Clonar (duplicar), Info (detalles e historial) y Eliminar. Las acciones se desactivan mientras un informe esta en ejecucion.'
			: 'Actions per report: Perform (run now), Edit (change config), Clone (duplicate), Info (details & history), and Delete. Actions are disabled while a report is running.',
		example: ES
			? 'Ejecutar | Editar | Clonar | Info | Eliminar'
			: 'Perform | Edit | Clone | Info | Delete',
	},
	// ─── 32: Show Report Info button ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-show-report-info"]') ||
			document.querySelector('[title="Show Report Info"]')
		),
		skipIf: () => !reportListData.value.length,
		icon: 'circle-info',
		title: ES ? 'Boton Informacion del Informe' : 'Show Report Info Button',
		description: ES
			? 'El boton "i" en la columna Acciones abre el panel de detalles completos del informe. Desde ahi puedes consultar toda la configuracion guardada y el historial de ejecuciones con sus resultados y descargas.'
			: 'The "i" button in the Actions column opens the full report details panel. From there you can view the complete saved configuration and the execution history with its results and downloads.',
		action: () => {
			const firstReport = reportListData.value[0]
			if (firstReport) {
				selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS
				isOpenInfoModal.value = firstReport.idReport
			}
		},
		waitFor: () => !!isOpenInfoModal.value && !!document.querySelector('.MuiDialog-root'),
	},
	// ─── 33: Info Modal — overview ───────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('.MuiDialog-root'),
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'rectangle-list',
		title: ES ? 'Panel de Informacion del Informe' : 'Report Info Panel',
		description: ES
			? 'Este modal muestra toda la informacion del informe en dos pestañas: "Report Details" con la configuracion completa guardada, y "Report History" con el historial de ejecuciones, sus estados y acciones de descarga. Util para auditar cambios o recuperar resultados anteriores.'
			: 'This modal shows all report information in two tabs: "Report Details" with the complete saved configuration, and "Report History" with the execution history, their statuses, and download actions. Useful for auditing changes or retrieving previous results.',
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 34: Info Modal — Tab Report Details ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const dialog = document.querySelector('.MuiDialog-root')
			if (!dialog) return null
			const tabs = dialog.querySelectorAll('[role="tab"]')
			for (const tab of tabs) {
				if (tab.textContent.toLowerCase().includes('details')) return tab
			}
			return null
		},
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'memo-circle-info',
		title: ES ? 'Pestaña Report Details' : 'Report Details Tab',
		description: ES
			? 'La pestaña "Report Details" muestra la configuracion completa tal como fue guardada: nombre, sitios, oficinas, moneda, rutas de solo ida, fechas de busqueda, duraciones, horas y dias de ejecucion, emails de notificacion y filtros de exportacion.'
			: 'The "Report Details" tab shows the complete configuration as saved: name, sites, offices, currency, one-way routes, search dates, durations, execution hours and days, notification emails, and export filters.',
		action: () => { selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS },
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 35: Info Modal — Tab Report History ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const dialog = document.querySelector('.MuiDialog-root')
			if (!dialog) return null
			const tabs = dialog.querySelectorAll('[role="tab"]')
			for (const tab of tabs) {
				if (tab.textContent.toLowerCase().includes('history')) return tab
			}
			return null
		},
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'rectangle-history',
		title: ES ? 'Pestaña Report History' : 'Report History Tab',
		description: ES
			? 'La pestaña "Report History" muestra todas las ejecuciones anteriores del informe en orden cronologico inverso. Para cada ejecucion ves la fecha, el estado (Listo, Detenido) y los botones de descarga. Perfecto para recuperar resultados de ejecuciones pasadas.'
			: 'The "Report History" tab shows all previous executions of the report in reverse chronological order. For each execution you see the date, the status (Ready, Stopped), and download buttons. Perfect for retrieving results from past executions.',
		action: () => {
			selectedInfoModalTab.value = INFO_MODAL_TABS.HISTORY
			isOpenInfoModal.value = null
		},
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 36: Multi-Select ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const checkboxes = document.querySelectorAll('th .MuiCheckbox-root, th input[type="checkbox"]')
			if (checkboxes.length) return checkboxes[0].closest('th')
			return null
		},
		icon: 'list-check',
		title: ES ? 'Seleccion Multiple' : 'Multi-Select',
		description: ES
			? 'Selecciona varios informes extra para aplicar acciones masivas: editar sitios, recogidas, emails, frecuencia, ejecutar, detener, copiar, eliminar, activar o desactivar — todo a la vez.'
			: 'Select multiple extra reports to apply bulk actions: edit sites, pickups, emails, frequency, perform, stop, copy, delete, enable, or disable — all at once.',
		example: ES
			? 'Selecciona filas → Barra de acciones masivas'
			: 'Select rows → Bulk toolbar with mass actions',
	},
	// ─── 37: Outro ──────────────────────────────────────────────────────────────
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Crea tu primer informe extra para empezar a monitorizar precios de rutas de solo ida. Usa el boton Info para consultar el historial de ejecucion y descargar informes anteriores.'
			: 'Create your first extra report to start monitoring one-way route pricing. Use the Info button to check execution history and download past reports.',
	},
]